"use client";

import Image, { type ImageProps } from "next/image";
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";

type Stage = "loading" | "revealing" | "done";

// Aligned with `--duration-fast` (250ms) from globals.css :root tokens.
// If the token changes, update this constant too — the value lives in JS
// for the setTimeout-driven stage transition, in CSS for the opacity ramp.
const REVEAL_MS = 250;

/**
 * Drop-in replacement for next/image. Renders a wrapper <span> that paints
 * the dot-pattern skeleton via ::before while the image inside fades in on
 * load — giving a true skeleton → image crossfade instead of a hard snap.
 *
 * Stage machine:
 *   loading   → img opacity 0, wrapper::before painting skeleton + shimmer
 *   revealing → img opacity 1 (250ms transition), shimmer frozen
 *   done      → wrapper class removed entirely
 *
 * Reduced-motion mode skips the revealing stage entirely (loading → done).
 * See `.image-skeleton-wrap` in globals.css.
 */
export default function SkeletonImage(props: ImageProps) {
  const [stage, setStage] = useState<Stage>("loading");
  const wrapRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const reducedMotion = useReducedMotion();

  // If the image was already cached by the browser before this component
  // mounted, `onLoad` won't fire on the React-managed element. Probe
  // `img.complete` after mount and skip straight to done if so.
  // Guarded by `prev === "loading"` so we never override an in-progress
  // reveal triggered by a racing onLoad.
  useEffect(() => {
    const img = wrapRef.current?.querySelector("img");
    if (img?.complete && img.naturalWidth > 0) {
      setStage((prev) => (prev === "loading" ? "done" : prev));
    }
  }, []);

  // Clean up the reveal timer on unmount so stale callbacks don't fire
  // setState on an unmounted component (common on fast route changes).
  useEffect(() => () => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
    }
  }, []);

  const handleLoad: ImageProps["onLoad"] = (e) => {
    if (reducedMotion) {
      setStage("done");
    } else {
      setStage((prev) => (prev === "loading" ? "revealing" : prev));
      timerRef.current = window.setTimeout(() => setStage("done"), REVEAL_MS);
    }
    if (typeof props.onLoad === "function") {
      props.onLoad(e);
    }
  };

  // In `fill` mode, Next.js Image renders the <img> with `position: absolute;
  // inset: 0` and expects its parent to be a positioned, sized box. Our
  // wrapper has to fill the call-site's container — `absolute inset-0` — or
  // the <img> collapses (containing block is the 0-height wrapper instead of
  // the outer container). In intrinsic-sized mode the wrapper just stays
  // inline-block/block and sizes to the img.
  // MIRROR the caller's radius onto the wrapper. The skeleton paints on
  // `.image-skeleton-wrap::before` at `inset: 0` with NO radius of its own, so
  // a rounded <img> sits inside a SQUARE grey placeholder and reads as a second
  // border while loading. Invisible at the old 2px; obvious at 12px, and
  // egregious on `rounded-full` (a circle in a grey box). Copying the radius
  // classes across costs nothing and fixes every call site at once.
  const radiusCls = (props.className ?? "")
    .split(/\s+/)
    // Variants may be hyphenated (`group-hover:`) or stacked
    // (`sm:hover:`), so match ANY run of `word:` prefixes — `[a-z]+:`
    // alone silently skipped both, and a silent skip here brings the
    // square-skeleton bug back with no signal.
    .filter((c) => /^(?:[a-z][a-z0-9-]*:)*rounded(?:-|$)/.test(c))
    .join(" ");

  const wrapClass = [
    "block",
    props.fill ? "absolute inset-0" : "relative",
    radiusCls,
    stage !== "done" ? "image-skeleton-wrap" : "",
    stage === "revealing" ? "is-revealing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const imgClass = [
    props.className ?? "",
    reducedMotion ? "" : "transition-opacity ease-out",
    stage === "loading" ? "opacity-0" : "opacity-100",
  ]
    .filter(Boolean)
    .join(" ");

  const imgStyle = reducedMotion
    ? props.style
    : { ...props.style, transitionDuration: `${REVEAL_MS}ms` };

  return (
    <span ref={wrapRef} className={wrapClass}>
      {/* `alt` listed explicitly so jsx-a11y/alt-text can verify it
          statically — spreading {...props} hides it from the rule. */}
      <Image
        {...props}
        alt={props.alt}
        className={imgClass}
        style={imgStyle}
        onLoad={handleLoad}
      />
    </span>
  );
}
